import React, { useEffect, useState } from "react";
import {
  FaFileInvoiceDollar,
  FaDollarSign,
  FaMoneyCheck,
  FaBuilding,
  FaChartLine,
  FaCalendarAlt,
  FaSync,
  FaReceipt,
  FaBalanceScale,
  FaLink,
  FaCog,
  FaUserShield,
  FaUsers,
} from "react-icons/fa";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import { MdInventory2 } from "react-icons/md";
import { GiMedicines } from "react-icons/gi";
import { toast } from "react-toastify";

const AdminDashboard = () => {
  const { activeSession } = useGlobalContext();
  const { requestWithAuth } = useAuth();
  const [gettingDashboardData, setGettingDashboardData] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  const getAdminDashboardData = async () => {
    setGettingDashboardData(true);
    try {
      const response = await requestWithAuth(`/users/admin/dashboard`, "GET");
      setDashboardData(response?.data);
    } catch (error) {
      toast.error("Failed to get dashboard data");
    } finally {
      setGettingDashboardData(false);
    }
  };

  useEffect(() => {
    getAdminDashboardData();
  }, []);

  const StatCard = ({
    title,
    value,
    subtitle,
    additionalInfo,
    icon: Icon,
    color,
    linkTo,
  }) => (
    <div
      className={`rounded-lg p-6 ${color} text-white shadow-lg hover:shadow-xl transition-shadow`}
    >
      <div className="flex items-center justify-between mb-4">
        <Icon className="text-2xl opacity-80" />
        <div className="text-right">
          <h3 className="text-2xl font-bold">{value}</h3>
          <p className="text-sm opacity-90">{title}</p>
        </div>
      </div>
      {subtitle && <p className="text-xs opacity-75 mb-2">{subtitle}</p>}
      {additionalInfo && (
        <p className="text-xs opacity-75 mb-3">{additionalInfo}</p>
      )}
      {linkTo && (
        <button
          onClick={() => (window.location.href = `${linkTo}`)}
          className="inline-flex items-center gap-1 text-sm hover:underline cursor-pointer"
        >
          Manage <FaLink className="text-[10px]" />
        </button>
      )}
    </div>
  );

  const SimpleCard = ({
    title,
    value,
    linkTo,
    color = "bg-gray-100",
    subtitle,
    icon: Icon,
  }) => (
    <div
      className={`rounded-lg p-6 ${color} text-center shadow-md hover:shadow-lg transition-shadow`}
    >
      <div className="flex justify-center mb-3">
        {Icon && <Icon className="text-3xl text-gray-600" />}
      </div>
      <h3 className="text-2xl font-bold mb-2 text-gray-800">{value}</h3>
      <p className="text-sm text-gray-600 mb-3">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
      {linkTo && (
        <button
          onClick={() => (window.location.href = `${linkTo}`)}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline cursor-pointer"
        >
          Access <FaLink className="text-[12px]" />
        </button>
      )}
    </div>
  );

  if (gettingDashboardData) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <FaUserShield className="text-blue-600" />
            Admin Dashboard
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-gray-200 rounded-lg h-36 animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <FaUserShield className="text-blue-600" />
          Admin Dashboard
        </h1>
        <p className="text-gray-600">
          Administrative control center for business operations
        </p>
      </div>

      {/* Core Administrative Functions */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <FaCog className="text-gray-600" />
          Core Administrative Functions
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Primary administrative operations and financial management
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Invoice Entries"
          value={dashboardData?.pending_invoices}
          subtitle={`Total Value: GHS ${dashboardData?.total_invoice_value}`}
          additionalInfo="Total Invoice Processed"
          icon={FaFileInvoiceDollar}
          color="bg-blue-600"
          linkTo="/inventory/invoice"
        />

        <StatCard
          title="Low stock drugs"
          value={dashboardData?.low_stock_drugs}
          additionalInfo="Drugs below reorder"
          icon={GiMedicines}
          color="bg-green-600"
          linkTo="/inventory/reorder/level"
        />

        <StatCard
          title="Branches"
          value={dashboardData?.active_branches}
          subtitle="Active registered branches"
          additionalInfo="Branch management & creation"
          icon={FaBuilding}
          color="bg-purple-600"
          linkTo="/administration/branches"
        />

        <StatCard
          title="This Month's Sales"
          value={`GHS ${dashboardData?.monthly_sales}`}
          subtitle="Sales details and analytics"
          additionalInfo="Comprehensive sales reporting"
          icon={FaChartLine}
          color="bg-orange-600"
          linkTo="/admin/sales/details"
        />
      </div>

      {/* Operational Management */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Operational Management
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Staff, branch operations, and system management
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <SimpleCard
          title="Registered Staff"
          value={`${dashboardData?.staff_count} Staff`}
          linkTo="/administration/users"
          color="bg-white border-2 border-blue-200 text-blue-800"
          icon={FaUsers}
        />

        <SimpleCard
          title="Company Inventory"
          value="Manage"
          subtitle="Set and update invoice pricing"
          linkTo="/administration/inventory"
          color="bg-white border-2 border-green-200 text-green-800"
          icon={MdInventory2}
        />
      </div>

      {/* Financial Control */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Financial Control & Interbranch
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Expense management and Interbranch transfer
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SimpleCard
          title="Expenses Entries"
          value={dashboardData?.pending_expenses}
          subtitle={`Total: GHS ${dashboardData?.expense_value}`}
          linkTo="/admin/expenses/entries"
          color="bg-gradient-to-br from-white-500 to-red-600 text-white"
          icon={FaReceipt}
        />
      </div>


    </div>
  );
};

export default AdminDashboard;
